import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { Country, CountryFilters } from '../../../app/models/Country';
import useCountryService from '../../../app/services/hooks/useCountryService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import DefaultToolBar from '../../../components/default/DefaultToolBar';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import CountryDataTable from './tables/CountryDataTable';
import DefaultModal from '../../../components/default/DefaultModal';
import CountryCreate from './CountryCreate';
import CountryEdit from './CountryEdit';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';

const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Configuraciones', url: '#', isActive: false },
    { name: 'Países', url: '/countries', isActive: true }
];

const CountryContainer = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const confirmAlert = useReactConfirmAlert();

    const { fetchingGetCountries, getCountries, deleteCountry } = useCountryService();

    const [countries, setCountries] = useState<Country[]>([]);
    const [countryIdEdit, setCountryIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [filter, setFilter] = useState<CountryFilters>({
        name: '',
        phone_prefix: '',
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'asc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter]);

    const reloadTable = () => {
        getCountries(filter, {
            onSuccess: (response: ServiceResponse) => {
                setCountries(response.data.countries);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data?.to_dashboard) navigationPage('/');
            }
        });
    };

    const showEdit = (id: number) => {
        setCountryIdEdit(id);
        setShowingEdit(true);
    };

    const hideEdit = () => {
        setCountryIdEdit(-1);
        setShowingEdit(false);
    };

    const showCreate = () => setShowingCreate(true);
    const hideCreate = () => setShowingCreate(false);

    const destroy = (id: number) => {
        confirmAlert.requestConfirmation({
            title: '¿Estás seguro?',
            message: `Está a punto de eliminar el país #${id}`,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        showLoading?.('loading', 'Eliminando país...');
                        deleteCountry(id, {
                            onSuccess: (res) => {
                                hideLoading?.();
                                confirmAlert.successAlert({ title: 'Éxito', message: res.message });
                                reloadTable();
                            },
                            onError: (res) => {
                                hideLoading?.();
                                confirmAlert.errorAlert({ title: 'Error', message: res.message });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            confirmAlert.infoAlert({
                                title: 'Cancelado',
                                message: 'El país no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Países" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo país" />}
                />
                <CountryDataTable
                    countries={countries}
                    edit={showEdit}
                    destroy={destroy}
                    active={reloadTable}
                    loading={fetchingGetCountries}
                    filter={filter}
                    setFilter={setFilter}
                    totalRows={totalRows}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear país"
                    backdrop={true}
                    showFooter={false}
                >
                    <CountryCreate
                        onSaved={() => { reloadTable(); hideCreate(); }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}

            {showingEdit && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar país"
                    backdrop={true}
                    showFooter={false}
                >
                    <CountryEdit
                        countryId={countryIdEdit}
                        onSaved={() => { reloadTable(); hideEdit(); }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default CountryContainer;
